import React, { useEffect, useState, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Typography,
  Tabs,
  Tab,
  CircularProgress,
  Paper,
} from "@material-ui/core";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import BulkSendPanel from "../../components/BulkSend/BulkSendPanel";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minHeight: 0,
    overflow: "hidden",
  },
  tabsWrapper: {
    flexShrink: 0,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  contentArea: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  iframe: {
    flex: 1,
    border: "none",
    width: "100%",
    minHeight: 0,
  },
  centered: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    gap: theme.spacing(2),
    padding: theme.spacing(4),
  },
}));

const CargaDeDatos = () => {
  const classes = useStyles();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [loggedSlots, setLoggedSlots] = useState(new Set());

  const fetchForms = useCallback(async () => {
    try {
      const { data } = await api.get("/company-data-forms");
      setForms(data);
    } catch (_) {
      setForms([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchForms();
  }, [fetchForms]);

  const fireAccessLog = useCallback((slot) => {
    if (loggedSlots.has(slot)) return;
    setLoggedSlots(prev => new Set(prev).add(slot));
    api.post("/company-data-forms/access-log", { slot }).catch(() => {});
  }, [loggedSlots]);

  useEffect(() => {
    if (forms.length > 0) {
      fireAccessLog(forms[0].slot);
    }
  }, [forms]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTabChange = (_, newIndex) => {
    setActiveTab(newIndex);
    if (forms[newIndex]) {
      fireAccessLog(forms[newIndex].slot);
    }
  };

  if (loading) {
    return (
      <div className={classes.centered}>
        <CircularProgress />
        <Typography variant="body2" color="textSecondary">
          {i18n.t("cargaDeDatos.loading")}
        </Typography>
      </div>
    );
  }

  if (forms.length === 0) {
    return (
      <div className={classes.centered}>
        <Typography variant="h6" color="textSecondary">
          {i18n.t("cargaDeDatos.empty")}
        </Typography>
      </div>
    );
  }

  const activeForm = forms[activeTab] || forms[0];

  return (
    <div className={classes.root}>
      {forms.length > 1 && (
        <div className={classes.tabsWrapper}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
          >
            {forms.map((f) => (
              <Tab key={f.slot} label={f.title} />
            ))}
          </Tabs>
        </div>
      )}

      <div className={classes.contentArea}>
        {forms.length === 1 && (
          <div style={{ padding: "12px 16px 8px", borderBottom: "1px solid #e5e7eb" }}>
            <Typography variant="h6">{activeForm.title}</Typography>
          </div>
        )}
        {activeForm.slotType === "bulk_send" ? (
          <BulkSendPanel
            key={activeForm.slot}
            profileId={activeForm.profileId}
            companyId={activeForm.companyId}
          />
        ) : (
          <iframe
            key={activeForm.slot}
            src={activeForm.url}
            className={classes.iframe}
            title={activeForm.title}
            frameBorder="0"
            allow="fullscreen"
          />
        )}
      </div>
    </div>
  );
};

export default CargaDeDatos;
